import { useEffect, useState } from 'react';
import { Box, Typography, Sheet, CircularProgress } from '@mui/joy';
import { CalendarBlank, Clock, CheckCircle, XCircle } from '@phosphor-icons/react';
import { APIGetDailyQuestion, APIAnswerDailyQuestion } from '../../api';
import { SafeHTML, formatExplanationHtml } from '../../utils';

/**
 * Вопрос дня на главной v2. Логика из MainPage, оформление под плитки главной.
 */
const NAVY = '#2A75D3';
const LIME = '#65A30D';
const CORAL = '#B84C31';
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

function useCountdown() {
    const [left, setLeft] = useState('');
    useEffect(() => {
        const calc = () => {
            const now = new Date(); const t = new Date(now); t.setDate(t.getDate() + 1); t.setHours(0, 0, 0, 0);
            const diff = t - now;
            setLeft(`${Math.floor(diff / 3600000)}ч ${Math.floor((diff % 3600000) / 60000)}м ${Math.floor((diff % 60000) / 1000)}с`);
        };
        calc(); const id = setInterval(calc, 1000); return () => clearInterval(id);
    }, []);
    return left;
}

export default function DailyQuestionCard({ token }) {
    const [dq, setDq] = useState(undefined);
    const [picked, setPicked] = useState(null);
    const [result, setResult] = useState(null);
    const countdown = useCountdown();
    const lang = (() => { try { return localStorage.getItem('testLang') || 'en'; } catch { return 'en'; } })();
    const t = (en, ru) => (lang === 'ru' && ru ? ru : en);
    const today = new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await APIGetDailyQuestion(token);
                if (alive) setDq(res?.success ? res : null);
            } catch (e) { console.error('DailyQuestionCard', e); if (alive) setDq(null); }
        })();
        return () => { alive = false; };
    }, [token]);

    const answer = async (id) => {
        if (picked !== null) return;
        setPicked(id);
        try {
            const res = await APIAnswerDailyQuestion(token, { selected_answer_ids: [id] });
            if (!res?.success) return;
            const answers = dq?.question?.answers || [];
            const total = res.community_stats?.total_answers || 1;
            setResult({
                is_correct: res.is_correct,
                explanation: res.explanation, explanation_ru: res.explanation_ru,
                distribution: answers.map((a, i) => ({
                    id: a.id, letter: LETTERS[i] || '', is_correct: a.id === res.correct_answer_id,
                    pct: Math.round(((res.answer_distribution?.[a.id] || 0) / total) * 100),
                })),
            });
        } catch (e) { console.error('DailyQuestionCard answer', e); }
    };

    const Countdown = () => (
        <Typography level="body-sm" sx={{ color: 'text.tertiary', display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Clock size={14} /> Следующий вопрос через {countdown}
        </Typography>
    );

    let body;
    if (dq === undefined) {
        body = <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size="sm" /></Box>;
    } else if (result) {
        const ok = result.is_correct;
        body = (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 700, color: ok ? LIME : CORAL }}>
                    {ok ? <CheckCircle size={18} weight="fill" /> : <XCircle size={18} weight="fill" />}
                    {ok ? 'Правильно' : 'Неправильно'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {result.distribution.map(d => (
                        <Box key={d.id} data-testid={`dist-${d.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ width: 18, fontSize: 12, fontWeight: 700, color: d.is_correct ? LIME : 'text.tertiary' }}>{d.letter}</Typography>
                            <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'divider', overflow: 'hidden' }}>
                                <Box sx={{ width: `${d.pct}%`, height: '100%', bgcolor: d.is_correct ? LIME : `${NAVY}66` }} />
                            </Box>
                            <Typography sx={{ width: 36, textAlign: 'right', fontSize: 12, color: 'text.secondary' }}>{d.pct}%</Typography>
                        </Box>
                    ))}
                </Box>
                {result.explanation && (
                    <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: 'background.level1', fontSize: 14, lineHeight: 1.55 }}>
                        <SafeHTML html={formatExplanationHtml(t(result.explanation, result.explanation_ru))} />
                    </Box>
                )}
                <Countdown />
            </Box>
        );
    } else if (dq && !dq.user_answered && dq.question?.answers?.length) {
        body = (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {dq.clinical_case_text && (
                    <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: 'background.level1', fontSize: 14, lineHeight: 1.55, color: 'text.secondary' }}>
                        <SafeHTML html={t(dq.clinical_case_text, dq.clinical_case_text_ru)} />
                    </Box>
                )}
                <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
                    <SafeHTML html={t(dq.question.text, dq.question.text_ru)} />
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {dq.question.answers.filter(a => a.is_active !== false).map((a, i) => (
                        <Box key={a.id} role="button" tabIndex={0} onClick={() => answer(a.id)}
                            aria-label={`${LETTERS[i]}. ${String(a.text).replace(/<[^>]+>/g, '')}`}
                            sx={{
                                display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.25, borderRadius: '10px',
                                border: '1px solid', borderColor: picked === a.id ? NAVY : 'divider',
                                bgcolor: picked === a.id ? `${NAVY}12` : 'transparent',
                                cursor: picked === null ? 'pointer' : 'default', fontSize: 14, lineHeight: 1.45,
                                '&:hover': picked === null ? { borderColor: NAVY, bgcolor: `${NAVY}08` } : {},
                            }}>
                            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: 13, mt: '1px' }}>{LETTERS[i]}</Typography>
                            <Box sx={{ flex: 1 }}><SafeHTML html={t(a.text, a.text_ru)} /></Box>
                        </Box>
                    ))}
                </Box>
                {dq.community_stats?.total_answers > 0 && (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Уже ответили: {dq.community_stats.total_answers} чел. · +15 XP</Typography>
                )}
            </Box>
        );
    } else if (dq?.user_answered) {
        body = (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Вы уже ответили на вопрос дня. Приходите завтра!</Typography>
                <Countdown />
            </Box>
        );
    } else {
        body = <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Вопрос дня скоро появится.</Typography>;
    }

    return (
        <Sheet variant="outlined" sx={{ p: { xs: 2, sm: 2.25 }, borderRadius: '16px', borderColor: 'divider', bgcolor: 'background.surface' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarBlank size={18} color={CORAL} weight="duotone" /> Вопрос дня
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{today}</Typography>
            </Box>
            {body}
        </Sheet>
    );
}
